import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  PositionsState,
  Position,
  PositionDetailResponse,
  PositionListFilters,
} from '../../types/positions';

const initialState: PositionsState = {
  items: [],
  selectedId: null,
  selectedDetail: null,
  total: 0,
  filters: {},
  loading: false,
  error: null,
  featureEnabled: null,
  paidTierRequired: false,
};

const positionsSlice = createSlice({
  name: 'positions',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },

    setPositions: (
      state,
      action: PayloadAction<{ positions: Position[]; total: number }>
    ) => {
      state.items = action.payload.positions;
      state.total = action.payload.total;
      state.loading = false;
    },

    upsertPosition: (state, action: PayloadAction<Position>) => {
      const idx = state.items.findIndex((p) => p.id === action.payload.id);
      if (idx !== -1) {
        state.items[idx] = action.payload;
      } else {
        state.items.unshift(action.payload);
        state.total += 1;
      }
    },

    removePosition: (state, action: PayloadAction<number>) => {
      state.items = state.items.filter((p) => p.id !== action.payload);
      state.total = Math.max(0, state.total - 1);
      if (state.selectedId === action.payload) {
        state.selectedId = null;
        state.selectedDetail = null;
      }
    },

    setSelectedId: (state, action: PayloadAction<number | null>) => {
      state.selectedId = action.payload;
      if (action.payload === null) {
        state.selectedDetail = null;
      }
    },

    setSelectedDetail: (
      state,
      action: PayloadAction<PositionDetailResponse | null>
    ) => {
      state.selectedDetail = action.payload;
      state.loading = false;
    },

    setFilters: (state, action: PayloadAction<PositionListFilters>) => {
      state.filters = action.payload;
    },

    setFeatureEnabled: (state, action: PayloadAction<boolean | null>) => {
      state.featureEnabled = action.payload;
    },

    setPaidTierRequired: (state, action: PayloadAction<boolean>) => {
      state.paidTierRequired = action.payload;
    },

    clearPositions: () => initialState,
  },
});

export const {
  setLoading,
  setError,
  setPositions,
  upsertPosition,
  removePosition,
  setSelectedId,
  setSelectedDetail,
  setFilters,
  setFeatureEnabled,
  setPaidTierRequired,
  clearPositions,
} = positionsSlice.actions;

export default positionsSlice.reducer;
